import { useEffect, useMemo, useState } from 'react';
import { DBI_TOPCHARTS, useDispatch } from 'umi';
import { useQueryDefinition } from '@intelligence/hooks/useQueryDefinition';

/**
 * Convert string array to options format
 */
const getOptionsFromStringArray = (data: string[]) =>
  data?.map((item) => ({
    value: item,
    text: item,
  })) || [];

const getLocaleTextMap = (data: string[]) => {
  const obj: { [key: string]: string } = {};
  data?.forEach((item) => {
    if (item && !obj[item]) {
      obj[item] = item;
    }
  });
  return obj;
};

/**
 * Get mobile game engine options from cond API with filter_game_engine target
 * API payload: { gameEngineMobile: { target: 'filter_game_engine', params: { entity_type: 'mobile' } } }
 * API response: { gameEngineMobile: ["AG05", "Abyss Engine", "Adobe AIR", ...] }
 */
function useMobileGameEngineOptions() {
  const dispatch = useDispatch();

  const [gameEngineList, setGameEngineList] = useState<string[]>([]);

  const gameEngineMobile = useQueryDefinition('gameEngineMobile');

  useEffect(() => {
    // Only process when data is available (not undefined)
    // Handle null, empty array, or valid array
    if (gameEngineMobile === undefined) {
      return;
    }

    try {
      const gameEngineData = Array.isArray(gameEngineMobile) ? gameEngineMobile : [];
      const sortedData = [...gameEngineData].sort((a, b) =>
        (a ?? '').localeCompare(b ?? ''),
      );
      setGameEngineList(sortedData);
    } catch (e) {
      console.error('Error processing mobile game engine data:', e);
      setGameEngineList([]);
    }
  }, [gameEngineMobile]);

  const mobileGameEngineOptions = useMemo(
    () => getOptionsFromStringArray(gameEngineList),
    [gameEngineList],
  );

  const mobileGameEngineLocaleTextMap = useMemo(
    () => getLocaleTextMap(gameEngineList),
    [gameEngineList],
  );

  useEffect(() => {
    if (Object.keys(mobileGameEngineLocaleTextMap).length > 0) {
      dispatch({
        type: `${DBI_TOPCHARTS}/setGameEngineLocaleTextMap`,
        payload: mobileGameEngineLocaleTextMap,
      });
    }
  }, [dispatch, mobileGameEngineLocaleTextMap]);

  return {
    mobileGameEngineOptions,
    mobileGameEngineLocaleTextMap,
  };
}

export default useMobileGameEngineOptions;
